// LegacyCameraTile — per-camera preview tile used on the legacy /cameras
// grid + per-site detail page. Renamed from CameraTile to make room for
// the v7 dashboard CameraTile primitive (see ./CameraTile.tsx).
//
// Preview source: periodic snapshot poll via useSnapshotPoll (LLD §8.3
// option c — owner-confirmed). Live WebRTC remains on CameraDetail
// only; the wall stays inside AVA's session-cap and TPS budgets even
// at 16+ tiles by:
//   - jittered polling per tile (30-60s),
//   - global concurrency cap (4 simultaneous fetches),
//   - pause-on-hidden via visibilitychange,
//   - graceful 403 / 404 / 5xx fallbacks (tile never crashes).
//
// The mocked CSS-gradient background is preserved as the visual
// scaffolding behind the <img> so the tile still feels populated while
// the first snapshot is in flight (and as a tasteful fallback when the
// camera is offline / has no recent snapshot).

import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { CameraMock } from "@/mocks/cameras";
import {
  useSnapshotPoll,
  type SnapshotPollStatus,
} from "./useSnapshotPoll";
import { useViewportPriority } from "./viewportPriority";
import "./LegacyCameraTile.css";

/**
 * Compact relative-time formatter for the snapshot age label. Operators
 * scan a wall of tiles in seconds, so we keep it terse: "now", "12s",
 * "3m", "1h+". Anything older than an hour clamps to "1h+" — by then
 * the freshness signal stops being actionable and a longer label would
 * just add noise.
 */
export function formatSnapshotAge(
  lastUpdatedAt: number | null,
  now: number,
): string {
  if (lastUpdatedAt === null) return "—";
  const deltaMs = Math.max(0, now - lastUpdatedAt);
  if (deltaMs < 5_000) return "now";
  const seconds = Math.floor(deltaMs / 1_000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  return "1h+";
}

export interface LegacyCameraTileProps {
  camera: CameraMock;
  size?: "sm" | "md" | "lg";
  /**
   * If false, the tile is rendered as a static div (no click target). Used
   * inside the detail player where the tile is already the main content.
   */
  clickable?: boolean;
}

interface SnapshotFallbackProps {
  status: SnapshotPollStatus;
}

/**
 * Inline fallback UI rendered when the snapshot poll has no current
 * image to show. Each branch picks a glyph + label that signals the
 * underlying state without surfacing a stack trace to operators.
 *
 * Kept as plain CSS-styled glyphs (no icon library — upstream doesn't
 * pull one in, and adding a dep for four glyphs is over-engineering).
 */
function SnapshotFallback({ status }: SnapshotFallbackProps): JSX.Element {
  if (status === "loading") {
    return (
      <div
        className="cam-tile__snapshot-fallback cam-tile__snapshot-fallback--loading"
        aria-live="polite"
      >
        <span className="cam-tile__shimmer" aria-hidden="true" />
      </div>
    );
  }
  if (status === "no-access") {
    return (
      <div
        className="cam-tile__snapshot-fallback"
        role="status"
      >
        <span className="cam-tile__fallback-glyph" aria-hidden="true">
          {/* lock-ish glyph */}
          ⊘
        </span>
        <span className="cam-tile__fallback-label">No preview · access</span>
      </div>
    );
  }
  if (status === "no-preview") {
    return (
      <div
        className="cam-tile__snapshot-fallback"
        role="status"
      >
        <span className="cam-tile__fallback-glyph" aria-hidden="true">
          {/* camera-off-ish glyph */}
          ⌀
        </span>
        <span className="cam-tile__fallback-label">No preview yet</span>
      </div>
    );
  }
  // status === "error"
  return (
    <div className="cam-tile__snapshot-fallback" role="status">
      <span className="cam-tile__fallback-glyph" aria-hidden="true">
        !
      </span>
      <span className="cam-tile__fallback-label">Snapshot unavailable</span>
    </div>
  );
}

export function LegacyCameraTile({
  camera,
  size = "md",
  clickable = true,
}: LegacyCameraTileProps): JSX.Element {
  // Periodic snapshot — see useSnapshotPoll for the timer / semaphore /
  // visibility / cleanup contract. Disabled for offline cameras so we
  // don't waste BFF round trips on devices the catalog already says
  // aren't reachable.
  const isOffline = camera.status === "offline";
  // Viewport priority: "visible" tiles fetch first, "near" tiles
  // prefetch as soon as the visible band has slack, "far" tiles defer
  // entirely until they scroll into the near band. The IO observer is
  // pinned to the tile's outer container — disabled for offline tiles
  // so we don't pay for an observer that will never gate any fetch.
  const tileRef = useRef<HTMLDivElement | null>(null);
  const { priority, priorityRef } = useViewportPriority(tileRef, {
    enabled: !isOffline,
  });
  const { src, status: snapshotStatus, lastUpdatedAt } = useSnapshotPoll({
    deviceId: camera.camera_id,
    enabled: !isOffline,
    priority,
    priorityRef,
  });

  // Age-label ticker — only ticks while a snapshot is on-screen, and
  // slows from 1s to 30s once the snapshot is older than a minute
  // (the label collapses to "Nm"/"1h+" past that point so finer
  // resolution would just burn renders).
  const [now, setNow] = useState<number>(() => Date.now());
  useEffect(() => {
    if (lastUpdatedAt === null) return;
    const ageMs = Date.now() - lastUpdatedAt;
    const intervalMs = ageMs < 60_000 ? 1_000 : 30_000;
    const t = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(t);
  }, [lastUpdatedAt]);

  const showImage = !isOffline && snapshotStatus === "ok" && src !== null;
  const showFallback = !isOffline && !showImage;

  const tile = (
    <div
      ref={tileRef}
      className={`cam-tile cam-tile--${size} cam-tile--${camera.status}`}
      style={{ background: camera.preview_gradient }}
      aria-label={`${camera.name} at ${camera.location_id}`}
    >
      {/* Snapshot image — fades in over the gradient when ready. */}
      {showImage && (
        <img
          src={src ?? undefined}
          alt={`Latest snapshot from ${camera.name}`}
          className="cam-tile__snapshot"
          referrerPolicy="no-referrer"
          // Decode off the main thread so a wall full of fresh JPEGs
          // doesn't jank the UI when they all paint at once.
          decoding="async"
          // Native lazy load defers the CDN fetch for tiles below the
          // fold; the snapshot URL itself is already minted, so this
          // costs nothing and saves bandwidth on long location lists.
          loading="lazy"
        />
      )}

      {/* Fallback overlay — shimmer / lock / camera-off / error. */}
      {showFallback && <SnapshotFallback status={snapshotStatus} />}

      {/* Scanline overlay — pure CSS, gives the feed texture */}
      <div className="cam-tile__scanlines" aria-hidden="true" />

      {/* Top-left: camera name. The legacy mock-fleet "view label" was a
          synthetic prefix derived from the doorbot id; on real AVA cameras
          the name already conveys identity, so we drop the redundant
          uppercase chip rather than show e.g. "AVA1" above the same name. */}
      <div className="cam-tile__tl">
        <div className="cam-tile__name">{camera.name}</div>
      </div>

      {/* Top-right: snapshot freshness ("12s" / "3m") + non-online badges.
          The previous LIVE/clock pair implied real-time video; the wall
          is still images on a 30–60s poll, so we surface an explicit
          age instead. */}
      <div className="cam-tile__tr">
        {camera.status === "offline" ? (
          <span className="cam-tile__badge cam-tile__badge--off">OFFLINE</span>
        ) : camera.status === "degraded" ? (
          <span className="cam-tile__badge cam-tile__badge--warn">LOW BITRATE</span>
        ) : (
          <span
            className="cam-tile__age"
            title={
              lastUpdatedAt === null
                ? "Awaiting first snapshot"
                : `Last snapshot ${new Date(lastUpdatedAt).toLocaleString()}`
            }
          >
            {formatSnapshotAge(lastUpdatedAt, now)}
          </span>
        )}
      </div>

      {/* Bottom-left: camera name (full, in case the top-left got
          ellipsized at small tile sizes). The raw doorbot id is too long
          to be useful here; operators copy it from the detail page. */}
      <div className="cam-tile__bl">
        <span className="cam-tile__id" title={camera.camera_id}>
          {camera.name}
        </span>
      </div>
    </div>
  );

  if (!clickable) return tile;

  return (
    <Link
      to={`/cameras/${encodeURIComponent(camera.location_id)}?camera=${encodeURIComponent(camera.camera_id)}`}
      className="cam-tile__link"
      aria-label={`Open ${camera.name}`}
    >
      {tile}
    </Link>
  );
}
